import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const CONSENT_KEY = "cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

export const getConsentStatus = (): ConsentStatus => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CONSENT_KEY) as ConsentStatus;
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Don't show if already consented
    const status = getConsentStatus();
    if (status !== null) return;

    // Lazy load on scroll to avoid PageSpeed impact
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShouldRender(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    // Also show after 5 seconds as fallback
    const timeout = setTimeout(() => {
      setShouldRender(true);
      window.removeEventListener("scroll", handleScroll);
    }, 5000);

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (shouldRender) {
      // Small delay for smooth appearance
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [shouldRender]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setIsVisible(false);
  };

  if (!shouldRender || !isVisible) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Cookie Consent"
    >
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="font-heading font-semibold text-foreground">
            Cookie-Einstellungen
          </h3>
          <button
            onClick={handleReject}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          Wir verwenden Google Analytics, um unsere Website zu verbessern. 
          Es werden keine Marketing-Cookies gesetzt.{" "}
          <a 
            href="/datenschutz" 
            className="text-primary hover:underline"
          >
            Datenschutzerklärung
          </a>
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleAccept}
            className="flex-1"
            size="sm"
          >
            Akzeptieren
          </Button>
          <Button
            onClick={handleReject}
            variant="outline"
            className="flex-1"
            size="sm"
          >
            Nur Essenzielle
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
